import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { get, isEmpty } from "lodash";
import { withTranslation, useTranslation } from "react-i18next";
import validator from "validator";
import Select from "react-select";
import FloatInput from "./FloatInput";
import { Modal } from "react-responsive-modal";
import { usePreCheckInContext } from "./contexts/PreCheckInContext";
import BookingValidation from "./BookingValidation";
import NationalityPicker from "./GuestSteps/NationalityPicker";
import PhoneCodePicker from "./GuestSteps/PhoneCodePicker";
import SignatureSelector from "../../components/SignatureSelector";
import AddressPicker from "../../components/AddressPicker";
import Terms from "./components/Terms";

const GuestDataCollection = ({ match }) => {
  const initialFormState = {
    title: "Mr",
    firstName: "",
    lastName: "",
    email: "",
    country: "",
    countryCode: "",
    nationality: "",
    nationalityCode: "",
    phoneCode: "0",
    phone: "",
    birthday: "",
    passport: "",
    dateOfExpiry: "",
    tmSix: "",
    document: "",
    signature: "",
    clientSecret: "",
    stripeSubmitted: false,
    verified: false,
    idType: "",
    terms: [],
    completed: false,
    address: null,
  };

  const {
    propertyUid,
    bookingNumber,
    language,
    data: { guestService, configuration, presets, booking, guests },
    components: { PreCheckInHeader, PreCheckInContainer },
    func: { updateGuests, useFormInput },
  } = usePreCheckInContext();

  const history = useHistory();
  const { t } = useTranslation();
  const [formData, handleFormChange, setFormValues] =
    useFormInput(initialFormState);
  const [isPageMounted, setIsPageMounted] = useState(true);
  const [guestNumber, setGuestNumber] = useState(1);
  const [allTerms, setAllTerms] = useState([]);
  // const [terms, setTerms] = useState([]);
  const [primaryGuest, setPrimaryGuest] = useState(false);
  const [globalError, setGlobalError] = useState(null);
  const [errorsValidation, setErrorsValidation] = useState({});
  const [currentGuest, setCurrentGuest] = useState({});
  const [nationalityModal, setNationalityModal] = useState(false);
  const [phoneModal, setPhoneModal] = useState(false);
  const [addressModal, setAddressModal] = useState(false);
  const [canvas, setCanvas] = useState();

  const getTerms = useCallback(() => {
    let allTerms = [],
      requiredTerms = [];
    if (presets.appFeatureEnableSaveIdVerificationToProfile) {
      allTerms.push({
        name: "saveVerificationToProfile",
        defaultText: t(
          "SAVE_VERIFICATION_TO_PROFILE",
          "Save verification to profile"
        ),
      });
    }
    if (configuration && configuration.optInTermsTitle.length > 0) {
      allTerms.push({
        name: "optInTerms",
        defaultText: configuration.optInTermsTitle,
      });
    }

    let title = ["Privacy Statement"];
    let link = [guestService.privacyStatementLink || "https://www.okkami.com/privacy-policy"];
    if (presets.appAuthenticationType === "GHA") {
      title.push("Privacy Policy");
      link.push(guestService.privacyPolicyLink || "https://www.okkami.com/privacy-policy");
    }

    guestService.guestsTerms.forEach((term, index) => {
      let name = `terms${index + 1}`;
      allTerms.push({
        name,
        title: get(term, "link_label_title"),
        popupText: get(term, "popup_text"),
      });
      requiredTerms[name] = "";
    });

    const param_1 = t("BY_SUBMIT_FORM_1");
    const param_2 = t("BY_SUBMIT_FORM_2");
    const param_3 = t("BY_SUBMIT_FORM_3");
    const param_4 = t("BY_SUBMIT_FORM_4");
    const regexForm = [param_2, param_3, param_4];
    let terms0 = { regexForm };
    if (!isEmpty(param_1)) {
      terms0 = {
        name: "terms0",
        title: param_1,
        ...terms0,
      };
      requiredTerms["terms0"] = "";
    }
    allTerms.push(terms0);

    setAllTerms(allTerms);
    return requiredTerms;
  }, [configuration, guestService, presets, t]);

  useEffect(() => {
    const { guestNumber: matchedGuestNumber } = match.params;
    setGuestNumber(matchedGuestNumber);
    setPrimaryGuest(matchedGuestNumber === "1");
    if (guestService && guests && isPageMounted) {
      setIsPageMounted(false);
      const currGuest = guests.allGuests()[matchedGuestNumber - 1];
      setCurrentGuest(currGuest);

      const guestData = {
        title: currGuest.title || "Mr",
        firstName: currGuest.firstName,
        lastName: currGuest.lastName,
        email: currGuest.email,
        country: currGuest.country,
        countryCode: currGuest.countryCode,
        nationality: currGuest.nationality,
        nationalityCode: currGuest.nationalityCode,
        phoneCode: currGuest.phoneCode,
        phone: currGuest.phone,
        birthday: currGuest.birthday,
        passport: currGuest.passport,
        dateOfExpiry: currGuest.dateOfExpiry,
        tmSix: currGuest.tmSix,
        document: currGuest.document,
        signature: currGuest.signature,
        clientSecret: currGuest.clientSecret,
        idType: currGuest.idType,
        stripeSubmitted: currGuest.stripeSubmitted,
        verified: currGuest.verified,
        saveVerificationToProfile: currGuest.saveVerificationToProfile,
        address: currGuest.address,
        terms: getTerms(),
      };
      setFormValues({ ...formData, ...guestData });
    }
  }, [
    match.params,
    propertyUid,
    isPageMounted,
    guests,
    formData,
    setFormValues,
    guestService,
    getTerms,
  ]);

  const handleValidation = (fields) => {
    let errors = {};
    let exclude = [];
    let globalError = null;
    fields.forEach((field) => {
      if (field["validates"]) {
        if (field["validates"].split("|").includes("required")) {
          if (
            Array.isArray(formData[field["name"]]) ||
            formData[field["name"]] instanceof Object
          ) {
            for (const [key, value] of Object.entries(
              formData[field["name"]]
            )) {
              if (!value) {
                errors[key] = [];
                errors[key].push(
                  t(
                    "PLEASE_FILL_OUT_REQUIRED",
                    "Please fill all the required fields"
                  )
                );
              }
            }
          } else if (validator.isEmpty(formData[field["name"]])) {
            errors[field["name"]] = [];
            errors[field["name"]].push(
              t(
                "PLEASE_FILL_OUT_REQUIRED",
                "Please fill all the required fields"
              )
            );
          }
        }
        if (
          field["name"] === "email" &&
          field["validates"].split("|").includes("email")
        ) {
          if (guestService.allowWithoutEmail) {
            delete errors["email"];
          } else if (
            formData[field["name"]] &&
            !validator.isEmail(formData[field["name"]])
          ) {
            if (errors[field["name"]] === undefined) {
              errors[field["name"]] = [];
            }
            errors[field["name"]].push(
              t("INVALID_EMAIL_GUEST", "Please enter a valid email")
            );
          }

          if (
            !primaryGuest &&
            [...guests.allGuests()].some(
              (guest, index) =>
                index !== guestNumber - 1 && guest?.email === formData.email
            )
          ) {
            if (!errors[field["name"]]) errors[field["name"]] = [];
            errors[field["name"]].push(
              t(
                "ERROR_EMAIL_DUPLICATED_GUEST",
                "Error: The email is duplicated with another guest"
              )
            );
          }
        }

        // eslint-disable-next-line no-control-regex
        const rForeign = new RegExp("[^\u0000-\u007f]");
        if (
          formData[field["name"]] &&
          rForeign.test(formData[field["name"]]) &&
          !guestService.enableNonLatinChar
        ) {
          globalError = t(
            "REQUIRED_ENGLISH_TEXT",
            "Required fields - Please fill in with English language"
          );
        }
      }
    });

    if (!guestService.enableSignature || !guestService.requiredSignature) {
      exclude.push("signature");
    }

    if (!guestService.phoneRequired) {
      exclude.push("phoneCode");
      exclude.push("phone");
    }

    if (exclude.length > 0 && errors)
      errors = Object.fromEntries(
        Object.entries(errors).filter(([key, value]) => !exclude.includes(key))
      );

    setErrorsValidation(errors);
    setGlobalError(globalError);
    return Object.keys(errors).length === 0 && globalError === null;
  };

  const goToNextStep = () => {
    if (handleValidation(requiredFields())) {
      window.scrollTo(0, 0);
      saveCurrentGuest();
      if (guestService.enablePreferenceCollection) goToPreferences();
      else goToConfirm();
    }
  };

  const goToPreferences = () => {
    history.push(
      `/pre-checkin/${propertyUid}/book-detail/${bookingNumber}/guests/${guestNumber}/preferences?language=${language}`
    );
  };

  const goToConfirm = () => {
    history.push(
      `/pre-checkin/${propertyUid}/book-detail/${bookingNumber}/guests/${guestNumber}/confirm?language=${language}`
    );
  };

  const saveCurrentGuest = () => {
    const guestData = {
      ...currentGuest,
      title: formData.title,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      country: formData.country,
      countryCode: formData.countryCode,
      nationality: formData.nationality,
      nationalityCode: formData.nationalityCode,
      phoneCode: formData.phoneCode,
      phone: formData.phone,
      birthday: formData.birthday,
      passport: formData.passport,
      dateOfExpiry: formData.dateOfExpiry,
      tmSix: formData.tmSix,
      document: formData.document,
      signature: formData.signature,
      clientSecret: formData.clientSecret,
      stripeSubmitted: formData.stripeSubmitted,
      verified: formData.verified,
      idType: formData.idType,
      saveVerificationToProfile: formData.saveVerificationToProfile,
    };
    setCurrentGuest(guestData);
    guests.UpdateByNumber(guestNumber - 1, guestData);
    updateGuests(guests);
  };

  const requiredFields = () => {
    let fieldNames = BookingValidation().collection;
    return BookingValidation().fields.filter((field) =>
      fieldNames.includes(field["name"])
    );
  };

  const nationalityHandler = (value) => {
    if (value) {
      setNationalityModal(false);
      setFormValues({
        ...formData,
        nationalityCode: value.alpha_2_code,
        nationality: value.nationality,
      });
    }
  };

  const phoneHandler = (value) => {
    if (value) {
      setPhoneModal(false);
      setFormValues({
        ...formData,
        phoneCode: value.phone,
      });
    }
  };

  const signatureHandler = (value) => {
    if (value) {
      setFormValues({
        ...formData,
        signature: value,
      });
    }
  };

  const disabledPrimaryGuest = (email) => {
    for (const index in presets.otaEmailList) {
      const otaEmailRegex = presets.otaEmailList[index];
      const emailRegex = get(otaEmailRegex, "regex");
      if (emailRegex) {
        const regex = new RegExp(emailRegex);
        if (email.match(regex)) {
          return false;
        }
      }
    }
    return true;
  };

  const options = [
    { value: "Mr", label: t("MR", "Mr") },
    { value: "Mrs", label: t("MRS", "Mrs") },
    { value: "Ms", label: t("MS", "Ms") },
    { value: "Miss", label: t("MISS", "Miss") },
  ];
  const countryClassNames = {
    control: {
      paddingLeft: "0",
    },
    closeButton: "close-button-modal",
    modal: "custom-modal",
    overlay: "overlay-modal",
  };

  const handleTitle = (target) => {
    const returnObj = { name: "title", value: target.value };
    handleFormChange({ target: returnObj });
  };

  const interfaceTypeIsHotelKey =
    get(configuration, "pmsInterfaceType") === "hotelkey";
  const addressOne = get(formData.address, "address_one", "");
  const addressTwo = get(formData.address, "address_two", "");
  const city = get(formData.address, "city_address", "");
  const state = get(formData.address, "state_address", "");
  const postalCode = get(formData.address, "postcode", "");

  const addressCompleted =
    !isEmpty(addressOne) &&
    (interfaceTypeIsHotelKey || !isEmpty(addressTwo)) &&
    !isEmpty(city) &&
    !isEmpty(state) &&
    !isEmpty(postalCode);

  const fullAddress = `${addressOne}${
    interfaceTypeIsHotelKey ? "" : `, ${addressTwo}`
  }, ${city}, ${state} ${postalCode}`;
  return (
    <>
      <PreCheckInContainer
        containerName="data-collection"
        backToHome>
        <PreCheckInHeader
          title={t("DATA_COLLECTION", "Data Collection")}
          headerLink
        />
        {booking && currentGuest && (
          <>
            <section
              id="content"
              className="relative flex flex-col px-9 w-full flex-1 md:flex-0 pt-6">
              <div className="md:max-w-7xl mx-auto w-full">
                <div className="flex flex-col justify-center pt-5">
                  <div className="mb-8">
                    <p className="type-regular medium">
                      {guestService.basicInformationDescription}
                    </p>
                  </div>
                  {globalError && (
                    <div className="mb-8">
                      <p className="text-14 text-red-500 pt-6">{globalError}</p>
                    </div>
                  )}
                  <div className="flex flex-wrap mb-12">
                    <div className="w-2/5">
                      <label
                        className="text-okkami-husk mb-6 type-title small"
                        htmlFor="title"
                        style={{ color: presets.backgroundColorTopBar }}>
                        {t("TITLE")}
                        <span className="text-gray-400 type-regular small pl-2">
                          {`*${t("REQUIRED")}`}
                        </span>
                      </label>
                      <Select
                        id="title"
                        name="title"
                        className="w-full pr-8 outline-none focus:outline-none type-normal large"
                        style={{ borderColor: presets.backgroundColorTopBar }}
                        options={options}
                        components={{ IndicatorSeparator: () => null }}
                        isSearchable={false}
                        styles={{
                          control: (styles) => ({
                            ...styles,
                            border: "none",
                            borderBottom: "1px #C1C1C1 solid",
                            borderRadius: "0px",
                            background: "transparent",
                          }),
                        }}
                        onChange={handleTitle}
                        defaultValue={{
                          value: formData.title,
                          label: t("MR"),
                        }}
                        isDisabled={
                          primaryGuest &&
                          (guestService.preventPrimaryGuestEditing ||
                            guestService.preventPrimaryGuestEmailEditing)
                        }
                      />
                      {errorsValidation && errorsValidation["title"] && (
                        <p className="text-14 text-red-500 pt-6">
                          {errorsValidation["title"].join(". ")}
                        </p>
                      )}
                    </div>

                    <div className="w-3/5 pt-5">
                      <FloatInput
                        id="firstName"
                        name="firstName"
                        label={t("FIRSTNAME", "FIRST NAME")}
                        span={`*${t("REQUIRED")}`}
                        classNameLabel="mb-6 type-title small"
                        classNameSpan="text-gray-400 type-regular small pl-2"
                        classNameInput="capitalize border-b w-full px-4 rounded-none outline-none focus:outline-none type-normal large md:mb-10"
                        onChange={handleFormChange}
                        value={formData.firstName}
                        labelColor={presets.backgroundColorTopBar}
                        borderColor={presets.backgroundColorTopBar}
                        disabled={
                          guestService.preventPrimaryGuestEditing &&
                          primaryGuest
                        }
                      />
                      {errorsValidation && errorsValidation["firstName"] && (
                        <p className="type-title medium text-red-500 pt-6">
                          {errorsValidation["firstName"].join(". ")}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mb-12">
                    <FloatInput
                      id="lastName"
                      name="lastName"
                      label={t("LAST_NAME", "LAST NAME")}
                      span={`*${t("REQUIRED")}`}
                      classNameLabel="mb-6 type-title small"
                      classNameSpan="text-gray-400 type-regular small pl-2"
                      classNameInput="capitalize border-b w-full px-4 rounded-none outline-none focus:outline-none type-normal large md:mb-10"
                      onChange={handleFormChange}
                      value={formData.lastName}
                      labelColor={presets.backgroundColorTopBar}
                      borderColor={presets.backgroundColorTopBar}
                      disabled={
                        guestService.preventPrimaryGuestEditing && primaryGuest
                      }
                    />
                    {errorsValidation && errorsValidation["lastName"] && (
                      <p className="type-title medium text-center text-red-500 pt-6">
                        {errorsValidation["lastName"].join(". ")}
                      </p>
                    )}
                  </div>

                  <div className="mb-12">
                    <FloatInput
                      id={"email"}
                      name={"email"}
                      label={t("EMAIL")}
                      span={`*${t("REQUIRED")}`}
                      classNameLabel="mb-6 type-title small"
                      classNameSpan="text-gray-400 type-regular small pl-2"
                      classNameInput="border-b w-full px-4 rounded-none outline-none focus:outline-none type-normal large md:mb-10"
                      onChange={handleFormChange}
                      value={formData.email}
                      labelColor={presets.backgroundColorTopBar}
                      borderColor={presets.backgroundColorTopBar}
                      disabled={
                        booking.email &&
                        primaryGuest &&
                        guestService.preventPrimaryGuestEditing &&
                        disabledPrimaryGuest(booking.email)
                      }
                    />
                    {errorsValidation && errorsValidation["email"] && (
                      <p className="type-title medium text-center text-red-500 pt-6">
                        {errorsValidation["email"].join(". ")}
                      </p>
                    )}
                  </div>

                  {guestService.enablePassport && (
                    <div className="flex flex-col mb-12">
                      <label
                        className="mb-3 type-title small"
                        style={{ color: presets.backgroundColorTopBar }}>
                        {t("NATIONALITY")}
                        <span className="text-gray-400 type-regular small pl-2">
                          {`*${t("REQUIRED")}`}
                        </span>
                      </label>
                      <p
                        className="w-full outline-none focus:outline-none type-normal large pb-4"
                        style={{
                          borderBottom: "1px solid #C1C1C1",
                          color: "inherit",
                        }}
                        onClick={() => setNationalityModal(true)}>
                        {formData.nationality}
                      </p>
                      {errorsValidation && errorsValidation["nationality"] && (
                        <p className="text-14 text-red-500">
                          {errorsValidation["nationality"].join(". ")}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex flex-col mb-12">
                    <p
                      className="mb-6 type-title small"
                      style={{ color: presets.backgroundColorTopBar }}>
                      {t("PHONE_NUMBER", "PHONE NUMBER")}
                      <span className="text-gray-400 type-regular small pl-2">
                        {`*${t("REQUIRED")}`}
                      </span>
                    </p>
                    <div className="flex space-x-4 h-14 mb-4">
                      <div className="w-8 flex items-center justify-center">
                        <span className="type-normal large">+</span>
                      </div>
                      <div className="w-16">
                        <p
                          className="text-center w-full px-4 py-3 outline-none focus:outline-none type-normal large h-full flex items-center justify-center"
                          style={{ backgroundColor: "#DBDBDB" }}
                          onClick={() => setPhoneModal(true)}>
                          {formData.phoneCode}
                        </p>
                      </div>
                      <div className="flex-1 flex items-end">
                        <input
                          type="number"
                          name="phone"
                          value={formData.phone}
                          onChange={handleFormChange}
                          className="border-b w-full appearance-none px-4 outline-none focus:outline-none type-normal large"
                          style={{ borderColor: presets.backgroundColorTopBar }}
                        />
                      </div>
                    </div>
                    {errorsValidation && errorsValidation["phone"] && (
                      <p className="text-14 text-red-500">
                        {errorsValidation["phone"].join(". ")}
                      </p>
                    )}
                  </div>

                  {guestService.enableAddressCollection && (
                    <>
                      <div className="mb-12">
                        <p className="type-regular medium">
                          {t("ADDRESS")}
                          {guestService.requiredAddressCollection && (
                            <span className="text-gray-400 type-regular small pl-2">
                              {`*${t("REQUIRED")}`}
                            </span>
                          )}
                        </p>
                        <div className="md:max-w-2xl text-center mx-auto p-10">
                          <button
                            className="mb-4 uppercase preCheckinSubTitle font-normal text-16 text-center w-full py-8 border-2 border-dashed rounded-3xl cursor-pointer"
                            style={{
                              borderColor: presets.backgroundColorTopBar,
                            }}
                            title={t("ADD_NOW")}
                            onClick={() => setAddressModal(true)}>
                            {!addressCompleted ? (
                              <>
                                <p>+</p>
                                <p>{t("ADD_NOW")}</p>
                              </>
                            ) : (
                              <p>{fullAddress}</p>
                            )}
                          </button>
                          {addressCompleted && (
                            <button
                              className="mb-4 capitalize preCheckinSubTitle font-normal text-16 text-center cursor-pointer underline"
                              style={{
                                borderColor: presets.backgroundColorTopBar,
                              }}
                              title={t("EDIT")}
                              onClick={() => setAddressModal(true)}>
                              {t("EDIT")}
                            </button>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {guestService.enableSignature && (
                    <div className="flex flex-col mb-14">
                      <p className="type-regular medium text-okkami-husk mb-6 bg-okkami-mercury px-4 py-6 text-okkami-dove-gray">
                        {t("SIGNATURE")}
                        <span className="text-gray-400 type-regular small pl-2">
                          {guestService.requiredSignature &&
                            `*${t("REQUIRED")}`}
                        </span>
                      </p>
                      <SignatureSelector
                        setSignature={signatureHandler}
                        getSignature={formData.signature}
                        setRef={setCanvas}
                        getRef={canvas || formData.signature}
                      />
                      {guestService.requiredSignature &&
                        errorsValidation &&
                        errorsValidation["signature"] && (
                          <p className="text-14 text-red-500 my-4">
                            {errorsValidation["signature"].join(". ")}
                          </p>
                        )}
                    </div>
                  )}

                  <Terms
                    title={guestService.personalInformationDescription}
                    titleColor={presets.titleColor}
                    subtitleColor={presets.subtitleColor}
                    appHomeTextCallToAction={presets.appHomeTextCallToAction}
                    allTerms={allTerms}
                    terms={formData.terms}
                    errors={errorsValidation}
                    handleChange={handleFormChange}
                    globalError={globalError}
                  />
                </div>
                <button
                  className={`app-button-ipad type-title medium ${presets.buttonStyleName}`}
                  style={{
                    backgroundColor: presets.backgroundColorTopBar,
                    color: presets.appHomeTextCallToAction,
                  }}
                  onClick={() => goToNextStep()}>
                  {t("CONTINUE")}
                </button>
              </div>
            </section>
            <button
              className="app-button-mobile type-title medium "
              style={{
                backgroundColor: presets.backgroundColorTopBar,
                color: presets.appHomeTextCallToAction,
                position: "fixed",
                bottom: 0,
                left: 0,
              }}
              onClick={() => goToNextStep()}>
              {t("CONTINUE")}
            </button>

            {guestService.enablePassport && (
              <>
                <Modal
                  open={nationalityModal}
                  onClose={() => {
                    setNationalityModal(false);
                    const html = document.querySelector("html");
                    html.style = "";
                  }}
                  center
                  blockScroll={true}
                  classNames={countryClassNames}>
                  <div className="relative">
                    <NationalityPicker
                      nationalityHandler={nationalityHandler}
                      getNationality={formData.nationalityCode}
                      styles={{ color: presets.backgroundColorTopBar }}
                    />
                  </div>
                </Modal>
              </>
            )}

            <Modal
              open={phoneModal}
              onClose={() => {
                setPhoneModal(false);
                const html = document.querySelector("html");
                html.style = "";
              }}
              center
              blockScroll={true}
              classNames={countryClassNames}>
              <div className="relative">
                <PhoneCodePicker
                  countryHandler={phoneHandler}
                  getPhoneCode={formData.phoneCode}
                  styles={{ color: presets.backgroundColorTopBar }}
                />
              </div>
            </Modal>

            <Modal
              open={addressModal}
              onClose={() => {
                setAddressModal(false);
                const html = document.querySelector("html");
                html.style = "";
              }}
              center
              blockScroll={true}
              classNames={countryClassNames}>
              <div className="relative">
                <AddressPicker match={match} onBack={() => setAddressModal(false)}
                  onSave={(address) => {
                    setAddressModal(false);
                    setFormValues({ ...formData, address });
                  }} />
              </div>
            </Modal>
          </>
        )}
      </PreCheckInContainer>
    </>
  );
};

export default withTranslation()(GuestDataCollection);
